<template>
  <div
    class="group relative flex cursor-pointer justify-between gap-4 overflow-hidden px-4 py-3"
    @click="openDialog"
  >
    <div class="flex items-center gap-4">
      <BaseMemberAvatar v-if="feedbackType == 'to'" :member="feedback.receiver" />
      <BaseMemberAvatar v-else :member="feedback.sender" />
      <div class="flex flex-col">
        <p class="text-body-large flex flex-row items-center">
          <FeedbackIcon :type="feedbackType" size="20" class="mr-1" />
          <span>
            {{ feedbackType == 'to' ? `${$t('common.to')}` : `${$t('common.from')}` }}
            {{ counterpartName }}
          </span>
        </p>
        <p class="text-body-medium text-on-surface-variant line-clamp-1 italic">
          {{ previewText }}
        </p>
      </div>
    </div>
    <div class="text-on-surface-variant flex shrink-0 items-center gap-2">
      <TimeAgo class="text-label-small" :date="feedback.updatedAt" />
      <BaseIcon class="">chevron_right</BaseIcon>
    </div>
    <BaseStateLayer />
  </div>
  <BaseDialog type="basic" :is-open="isOpen" @close="closeDialog">
    <DialogTitle
      as="div"
      class="text-title-large sm:text-headline-small flex flex-row items-center gap-2 py-2"
    >
      <BaseMemberAvatar v-if="feedbackType == 'to'" :member="feedback.receiver" />
      <BaseMemberAvatar v-else :member="feedback.sender" />
      <div class="flex flex-col pr-10">
        <p class="text-body-large flex flex-row items-center">
          <FeedbackIcon :type="feedbackType" size="20" class="mr-1" />
          <span>
            {{ feedbackType == 'to' ? `${$t('common.to')}` : `${$t('common.from')}` }}
            {{ counterpartName }}
          </span>
        </p>
      </div>
      <TimeAgo class="text-label-medium whitespace-nowrap" :date="feedback.updatedAt" />
    </DialogTitle>
    <div class="py-4">
      <TextualQuestion v-if="feedback.text" :initial-value="feedback.text" />
    </div>
    <template #dismiss>
      <BaseTextButton color="text" @click="closeDialog">{{ $t('common.close') }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import TimeAgo from '@/core/components/base/time-ago/TimeAgo.vue'
  import { computed } from 'vue'
  import { useUserStore } from '../../../../stores/user.store'
  import BaseStateLayer from '../../../../core/components/base/state-layer/BaseStateLayer.vue'
  import BaseDialog from '../../../../core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '../../../../core/components/base/dialog/use-dialog'
  import BaseTextButton from '../../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useWysiwygEditor } from '../../../../core/composables/useWysiwygEditor'
  import { DialogTitle } from '@headlessui/vue'
  import type { UnstructuredFeedbackItem } from '../../composables/useFeedbackHistory'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import TextualQuestion from '../show-given-feedback/scale-based-questions/TextualQuestion.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import FeedbackIcon from '@/core/components/feedback-icon/FeedbackIcon.vue'
  import { memberAbbreviatedName } from '@/core/models/member/member-name'

  interface Props {
    feedback: UnstructuredFeedbackItem
  }

  const props = defineProps<Props>()
  const userStore = useUserStore()

  const feedbackType = computed(() => {
    if (props.feedback.sender?.id === userStore.user.id) {
      return 'to'
    } else {
      return 'from'
    }
  })

  const { isOpen, closeDialog, openDialog } = useDialog()

  const { convertJSONToPlainText } = useWysiwygEditor()

  const previewText = computed(() => {
    if (props.feedback.text) {
      return convertJSONToPlainText(props.feedback.text)
    }
    return undefined
  })

  const counterpartName = computed(() => {
    if (feedbackType.value == 'to') {
      return memberAbbreviatedName(props.feedback.receiver)
    }
    return props.feedback.sender
      ? memberAbbreviatedName(props.feedback.sender)
      : 'Anonym'
  })
</script>
